import { BaseClass } from '../base/BaseClass';

export class Dropdown extends BaseClass {
  /**
   * @desc Holds the dropdown dom element.
   * @type {HTML Node}
   */
  dropdown;

  /**
   * @desc Holds the dropdown header dom element.
   * @type {HTML Node}
   */
  dropdownHeader;

  /**
   * @desc Holds the dropdown header p tag dom element.
   * @type {HTML Node}
   */
  dropdownDisplay;

  /**
   * @desc Holds the dropdown options dom elements.
   * @type {HTML Node}
   */
  dropdownOptions;

  /**
   * @desc Holds the dropdown option titles dom elements.
   * @type {HTML Node}
   */
  dropdownOptionTitles;

  /**
   * @desc Is the dropdown expanded or not.
   * @type {bool}
   */
  isExpanded = false;

  /**
   * @desc Dropdown expanded class name.
   * @type {String}
   */
  expandedClass = 'dropdown-expanded';

  /**
   * @desc Dropdown selected class name.
   * @type {String}
   */
  selectedClass = 'selected';

  /**
   * @class
   *
   * @extends {BaseClass}
   *
   * @desc Create a dropdown menu that looks more appealing than a standard HTML one.
   *
   * General layout
   *
   * <div class="custom-dropdown">
   *    <div class="dropdown-header">
   *         <p>{Selection header}</p>
   *         <i class="far fa-chevron-down"></i>
   *     </div>
   *     <div class="dropdown-options">
   *         <p
   *             class="dropdown-option" - "selected" is for the selected option.
   *         >{Dropdown option title}</p>
   *     </div>
   * </div>
   *
   * @param {HTMLNode} dropdownEl
   */
  constructor(identifier) {
    super(identifier);
    if (this.targetExists()) {
      this.dropdown = this.target;
      this.dropdownHeader = this.dropdown.querySelector('.dropdown-header');
      this.dropdownDisplay = this.dropdownHeader.querySelector('p');
      this.dropdownOptions = this.dropdown.querySelectorAll('.dropdown-option');
      this.dropdownOptionTitles = this.dropdown.querySelectorAll(
        '.dropdown-section-title'
      );
      this.dropdownHeader.addEventListener('click', this.toggleDropdown);
      this.dropdownAutoClose();
      this.handleOptionClick();
    }
  }

  /**
   * @desc Controls when the dropdown is closed if a click happens outside of it.
   */
  dropdownAutoClose = () => {
    window.addEventListener('click', (e) => {
      if (this.isExpanded) {
        const headerFocus = e.target === this.dropdownHeader;
        const titleFocus = this.cycleOptions(
          this.dropdownOptionTitles,
          e.target
        );
        const optionFocus = this.cycleOptions(this.dropdownOptions, e.target);
        if (!headerFocus && !titleFocus && !optionFocus) {
          this.closeDropdown();
        }
      }
    });
  };

  /**
   * @desc Handle the option clicks.
   */
  handleOptionClick = () => {
    this.dropdownOptions.forEach((option) => {
      option.addEventListener('click', () => {
        this.onChange(option);
        this.dropdownDisplay.innerText = option.innerText;
        this.dropdownOptions.forEach((diffOption) =>
          diffOption.classList.remove(this.selectedClass)
        );
        option.classList.add(this.selectedClass);
        this.closeDropdown();
      });
    });
  };

  /**
   * @desc Inject custom functionality for each class when a
   * dropdown option is selected.
   * @param {HTML Node} e
   */
  onChange = (dropdownOption) => {};

  /**
   * @desc Toggle the dropdown
   */
  toggleDropdown = () => {
    this.dropdown.classList.toggle(this.expandedClass);
    this.isExpanded = !this.isExpanded;
  };

  /**
   * @desc Close the dropdown
   */
  closeDropdown = () => {
    this.dropdown.classList.remove(this.expandedClass);
    this.isExpanded = false;
  };

  /**
   * @desc Checks to see if any of the dropdown options have been clicked
   * @param array [objects], object
   * @return boolean
   */
  cycleOptions = (nodes, target) => {
    let found = false;
    nodes.forEach((node) => {
      if (node === target) {
        found = true;
      }
    });
    return found;
  };

  /**
   * @desc Match the filter terms with the currently selected filter
   *
   * @param {string} term
   * @param {HTML Node} videoSelection
   * @returns {bool}
   */
  matchTerms = (term, filterSelection) => {
    if (term.toLowerCase().startsWith('all')) {
      return true;
    } else {
      const rawTerms = filterSelection
        .getAttribute('data-filter-terms')
        .toLowerCase();
      const terms = rawTerms.split('|');
      return terms.includes(term.toLowerCase());
    }
  };
}
